import { useCallback, useMemo } from "react";

import { useGetCompaniesSummary } from "@/hooks/queries/useGetCompaniesSummary";
import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import {
  COMPANY_MATCH_REASONS,
  CompanyMatchReason,
  CompanyNameMatch,
  companies,
} from "@/lib/companies";

// Tailwind tone for a match, shared by the banner and the search chip. Red
// reasons always win; a current Laborup placement softens the default blue
// to yellow.
export const companyMatchTone = (
  reason: CompanyMatchReason,
  isCurrentLaborupPlacement?: boolean,
): { container: string; content: string } => {
  if (
    reason === COMPANY_MATCH_REASONS.SAME_COMPANY ||
    reason === COMPANY_MATCH_REASONS.CURRENT_CUSTOMER
  ) {
    return { container: "border-red-200 bg-red-50", content: "text-red-700" };
  }
  if (isCurrentLaborupPlacement) {
    return {
      container: "border-yellow-200 bg-yellow-50",
      content: "text-yellow-700",
    };
  }
  return { container: "border-blue-200 bg-blue-50", content: "text-blue-700" };
};

/**
 * Shared "did this candidate work at a customer?" matching. Owns the customer
 * list fetch, normalized name map, and current-company lookup so the banner,
 * search badge, and work-experience rows all match identically.
 */
export const useCustomerCompanyMatches = () => {
  /* Database CRUD Operations */
  // Read
  const { data: companySummaries } = useGetCompaniesSummary();
  const companyId = useCurrentCompanyId();

  /* React Hooks */
  const companyNameMap = useMemo(
    () =>
      companySummaries
        ? companies.buildCompanyNameMap(companySummaries)
        : undefined,
    [companySummaries],
  );

  const currentCompanyName = useMemo(
    () => companySummaries?.find((c) => c.id === companyId)?.name,
    [companySummaries, companyId],
  );

  // Ranked matches for a full employer-name list (names[0] = most recent).
  // Returns undefined while the customer list is loading, null on no match.
  const findMatches = useCallback(
    (names: (string | null | undefined)[]) => {
      if (!companyNameMap || names.length === 0) return undefined;
      return companies.findAllCompanyMatches(
        companyNameMap,
        names,
        currentCompanyName,
      );
    },
    [companyNameMap, currentCompanyName],
  );

  // Match + reason for a single employer name (isCurrent = candidate still
  // works there). Undefined while loading, null on no match.
  const matchCompany = useCallback(
    (
      name: string | null | undefined,
      isCurrent?: boolean | null,
    ):
      | (CompanyNameMatch & { reason: CompanyMatchReason })
      | null
      | undefined => {
      if (!companyNameMap) return undefined;
      if (!name) return null;
      const match = companies.findCompanyMatch(companyNameMap, name);
      if (!match) return null;
      if (currentCompanyName && match.company === currentCompanyName) {
        return { ...match, reason: COMPANY_MATCH_REASONS.SAME_COMPANY };
      }
      if (isCurrent) {
        return { ...match, reason: COMPANY_MATCH_REASONS.CURRENT_CUSTOMER };
      }
      return { ...match, reason: COMPANY_MATCH_REASONS.OTHER };
    },
    [companyNameMap, currentCompanyName],
  );

  return { findMatches, matchCompany };
};
